import { useEffect, useState } from 'react';
import { Alert, Button, message } from 'antd';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowRight, BadgeDollarSign, FileWarning, Landmark, Lock, RefreshCw } from 'lucide-react';
import Layout from '../../Layout';
import { ktApi, today, useKt } from './ktStore';
import {
  dayCloseErrors, dayTotals, dmy, fmtMoney, invoiceCheck, invoiceQueue, isLocked, matchBank, matchPayoo, pendingTxns, rateOn, TAX_CLASS,
} from './ktLogic';
import { cardRows, debtRows, depositRows, missingStaff } from './ktCards';
import { execRows } from './ktExec';
import { DemoBar, Kpi, PageHeader, Panel } from './ktUi';

/** Tổng quan phòng Kế toán: hôm nay cần làm gì, theo đúng thứ tự chốt doanh thu */
export default function KeToanOverview() {
  const s = useKt();
  const date = today();
  const t = dayTotals(s, date);
  const closeErrors = dayCloseErrors(s, date);
  const queue = invoiceQueue(s);
  const blocked = queue.filter(b => invoiceCheck(s, b).errors.length);
  const adjust = s.bills.filter(b => b.invoice.status === 'needs_adjust');
  const payoo = matchPayoo(s.payoo, s.bills);
  const bank = matchBank(s.bank, s.bills);
  const unmatched = [...payoo.matches, ...bank.matches].filter(m => m.status !== 'matched').length;
  const reconciled = s.payoo.length > 0 || s.bank.length > 0;
  const locked = isLocked(s, date);
  const sample = s.services.find(x => x.taxClass === 'cosmetic');
  const rate = sample ? rateOn(s.rates, sample.id, date) : null;
  const nextRate = s.rates.filter(r => r.serviceId === sample?.id && r.from > date).sort((a, b) => a.from.localeCompare(b.from))[0];

  const posBills = s.bills.filter(b => b.posOrderId);
  const posToday = posBills.filter(b => b.date === date);
  const [pulling, setPulling] = useState(false);
  const pull = async (quiet = false) => {
    setPulling(true);
    const r = await ktApi.pullServerOrders();
    setPulling(false);
    if (!quiet) r.error ? message.error('Không đọc được đơn POS trên server') : message.success(`Đã đồng bộ: ${r.added} đơn mới, ${r.changed} đơn thay đổi`);
  };
  // Mở màn là đọc đơn hôm nay trên server: máy thu ngân khác máy kế toán
  useEffect(() => { pull(true); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const cards = cardRows(s, date);
  const expiring = cards.filter(r => r.warn === 'expiring').length;
  const expired = cards.filter(r => r.warn === 'expired').length;
  const deposits = depositRows(s, date).filter(r => r.state === 'overdue');
  const debts = debtRows(s, date).filter(r => r.locked);
  const casesWaiting = s.refundCases.filter(r => ['pending_l1', 'pending_l2', 'approved'].includes(r.status));
  const missing = missingStaff(s);

  const pendingShift = pendingTxns(s).length;
  const shiftsWaiting = s.shifts.filter(x => x.status === 'closed').length;
  const noSession = execRows(s, date).filter(r => r.check !== 'done').length;

  const steps = [
    { n: 1, title: 'Kế toán quầy đối soát đơn POS', desc: closeErrors.length ? `${closeErrors.length} bill thiếu thông tin cần bổ sung` : 'Bill hôm nay đã đủ mã giao dịch', warn: closeErrors.length > 0, to: '/ke-toan/counter' },
    { n: 2, title: 'Đối soát ca thực hiện', desc: noSession ? `${noSession} dịch vụ hôm nay chưa xong hoặc chưa có dữ liệu thực hiện` : 'Các dịch vụ hôm nay đã xong', warn: noSession > 0, to: '/ke-toan/sessions' },
    { n: 3, title: 'Chốt ca, gửi biên bản và bảng kê', desc: pendingShift ? `${pendingShift} giao dịch chưa chốt ca` : shiftsWaiting ? `${shiftsWaiting} biên bản chờ Kế toán nội bộ nhận` : 'Không còn giao dịch chưa chốt', warn: pendingShift + shiftsWaiting > 0, to: pendingShift ? '/ke-toan/shift' : '/ke-toan/shift-review' },
    { n: 4, title: 'Kế toán nội bộ duyệt khóa sổ', desc: locked ? `Đã khóa sổ ${dmy(date)}` : `Hôm nay chưa khóa sổ, có ${t.valid.length} bill hợp lệ`, warn: !locked, to: '/ke-toan/bills' },
    { n: 5, title: 'Đối soát Payoo và sao kê', desc: reconciled ? `${unmatched} dòng lệch cần xử lý` : 'Chưa nạp file đối soát', warn: !reconciled || unmatched > 0, to: '/ke-toan/reconcile' },
    { n: 6, title: 'Phát hành hóa đơn', desc: `${queue.length} bill chờ xuất, ${blocked.length} bill bị chặn, ${adjust.length} cần điều chỉnh`, warn: blocked.length + adjust.length > 0, to: '/ke-toan/invoices' },
    { n: 7, title: 'Thẻ liệu trình và nhật ký trừ thẻ', desc: `${expiring} thẻ sắp hết hạn, ${expired} thẻ quá hạn`, warn: expiring + expired > 0, to: '/ke-toan/cards' },
    { n: 8, title: 'Cọc và công nợ theo Mã KH', desc: `${deposits.length} cọc quá hạn, ${debts.length} khách còn nợ đang bị khóa tạo ca`, warn: deposits.length + debts.length > 0, to: '/ke-toan/deposits' },
    { n: 9, title: 'Duyệt hủy thẻ, hoàn tiền', desc: `${casesWaiting.length} hồ sơ đang xử lý`, warn: casesWaiting.length > 0, to: '/ke-toan/refunds' },
    { n: 10, title: 'Hoa hồng và tua ekip', desc: missing.bills.length + missing.usages.length ? `${missing.bills.length + missing.usages.length} chứng từ đã khóa chưa gán đủ người` : 'Tính từ doanh thu đã khóa sổ', warn: missing.bills.length + missing.usages.length > 0, to: '/ke-toan/commission' },
    { n: 11, title: 'Kê khai thuế GTGT', desc: 'Số liệu lấy từ hóa đơn đã xuất và hóa đơn mua vào', warn: false, to: '/ke-toan/tax-return' },
    { n: 12, title: 'Đối chiếu Sổ Thuế và Sổ Quản trị', desc: 'P&L hai sổ, giải trình chênh lệch đến từng chứng từ', warn: false, to: '/ke-toan/books' },
  ];

  return (
    <Layout>
      <div className="flex-1 flex flex-col min-w-0 gap-4">
        <PageHeader title="Kế toán" desc={`Chốt doanh thu ngày ${dmy(date)}: từ bill tại quầy đến khóa sổ, đối soát và hóa đơn`} />
        <DemoBar />

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <Kpi icon={<BadgeDollarSign size={20} />} tone="bg-emerald-50 text-emerald-600" label="Thu hôm nay (gồm VAT)" value={fmtMoney(t.gross)} hint={`Chưa VAT ${fmtMoney(t.net)} · VAT ${fmtMoney(t.vat)}`} />
          <Kpi icon={<Lock size={20} />} tone="bg-sky-50 text-sky-600" label="Sổ hôm nay" value={locked ? 'Đã khóa' : 'Đang mở'} hint={`${t.edited} bill đã sửa · ${t.cancelled} bill đã hủy`} />
          <Kpi icon={<Landmark size={20} />} tone="bg-amber-50 text-amber-600" label="Dòng đối soát lệch" value={reconciled ? unmatched : 'Chưa nạp'} hint="Payoo và sao kê ngân hàng" />
          <Kpi icon={<FileWarning size={20} />} tone="bg-rose-50 text-rose-600" label="Hóa đơn cần xử lý" value={queue.length + adjust.length} hint={`${adjust.length} cần điều chỉnh`} />
        </div>

        {s.posAlerts.map(a => (
          <Alert key={a.id} type="warning" showIcon closable message={a.text} onClose={() => ktApi.dismissPosAlert(a.id)} />
        ))}

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4 items-start">
          <Panel title="Việc cần làm hôm nay">
            <div className="divide-y divide-gray-100 dark:divide-gray-800">
              {steps.map(x => (
                <Link key={x.n} to={x.to} className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800 group">
                  <span className="w-7 h-7 rounded-full bg-[#3e91ab]/10 text-[#3e91ab] text-sm font-semibold flex items-center justify-center shrink-0">{x.n}</span>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{x.title} <span className="text-[11px] font-normal text-gray-400">{x.n <= 3 ? 'Kế toán quầy' : x.n <= 5 || (x.n >= 7 && x.n <= 10) ? 'Kế toán nội bộ' : 'Kế toán tổng hợp'}</span></div>
                    <div className={`text-xs flex items-center gap-1 ${x.warn ? 'text-amber-600' : 'text-gray-500'}`}>
                      {x.warn && <AlertTriangle size={12} className="shrink-0" />}{x.desc}
                    </div>
                  </div>
                  <ArrowRight size={16} className="text-gray-300 group-hover:text-[#3e91ab] shrink-0" />
                </Link>
              ))}
            </div>
          </Panel>

          <div className="flex flex-col gap-4">
          <Panel title="Nối từ POS" extra={<Button size="small" loading={pulling} icon={<RefreshCw size={13} />} onClick={() => pull()}>Đồng bộ</Button>}>
            <div className="px-4 py-3 text-sm space-y-1">
              <div className="text-gray-900 dark:text-gray-100"><b>{posToday.length}</b> bill từ POS hôm nay, tổng <b>{posBills.length}</b> bill đã nhận</div>
              <div className="text-xs text-gray-500">POS trên máy này: {s.posSync.at ? `nhận tự động, lần cuối ${dayjs(s.posSync.at).format('HH:mm DD/MM')}` : 'chưa nhận'}</div>
              <div className="text-xs text-gray-500">POS trên server: {s.posSync.serverAt ? `${s.posSync.serverOrders} đơn hôm nay, đọc lúc ${dayjs(s.posSync.serverAt).format('HH:mm')}` : 'chưa đọc'}</div>
              {s.posSync.serverError && <div className="text-xs text-rose-600">{s.posSync.serverError}</div>}
            </div>
          </Panel>
          <Panel title="Thuế suất đang áp dụng">
            <div className="px-4 py-3 text-sm space-y-1">
              <div className="text-gray-900 dark:text-gray-100">{TAX_CLASS.cosmetic}: <b>{rate?.rate}%</b></div>
              {nextRate && <div className="text-xs text-gray-500">Tự chuyển sang {nextRate.rate}% từ {dmy(nextRate.from)}, không cần sửa bảng giá</div>}
              <Link to="/ke-toan/tax-rates" className="text-xs text-[#3e91ab] hover:underline inline-block pt-1">Xem bảng thuế suất</Link>
            </div>
          </Panel>
          </div>
        </div>
      </div>
    </Layout>
  );
}
